import {
  column,
  Css,
  FilterDefs,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  multiFilter,
  numericColumn,
  simpleHeader,
  toggleFilter,
  useGridTableLayoutState,
} from "@homebound/beam";
import { useMemo } from "react";
import { emptyCellDash, MarkupSummary, Price } from "src/components";
import { ReviewImpactedPrice } from "../../components/ReviewImpactedPrice";
import { ReviewProjectImpact_CostCodeFragment } from "../3.ReviewImpactStep.generated";

/** One row of the Project Budget table, i.e. what a single cost code costs today vs. after the configuration. */
type ProjectBudgetRow = {
  costCode: ReviewProjectImpact_CostCodeFragment;
  currentBudgetInCents: number;
  newBudgetInCents: number;
  /** Undefined when the branch has no price data, so Markup/Price render as a dash. */
  newPriceInCents: number | undefined;
};

type ProjectBudgetTableProps = { budgetRows: ProjectBudgetRow[] };

/** The per-cost-code "what the project's budget looks like before and after this configuration" table. */
export function ProjectBudgetTable(props: ProjectBudgetTableProps) {
  const { budgetRows } = props;

  const filterDefs: FilterDefs<ProjectBudgetFilter> = useMemo(() => {
    const costCodeIds = budgetRows
      .map((r) => r.costCode)
      .unique()
      .sortByKey("displayName");
    return {
      costCodeIds: multiFilter({
        label: "Cost Code",
        options: costCodeIds,
        getOptionLabel: ({ displayName }) => displayName,
        getOptionValue: ({ id }) => id,
      }),
      showChanges: toggleFilter({ label: "Only Show Changes", defaultValue: false }),
    };
  }, [budgetRows]);

  const layoutState = useGridTableLayoutState<ProjectBudgetFilter>({
    persistedFilter: { filterDefs, storageKey: "reviewImpactBudgetFilter" },
    search: "client",
  });
  const { filter } = layoutState;

  // The totals row is always the whole configuration's impact, even while the table is filtered down
  const rows = useMemo(() => createRows(budgetRows, filter), [budgetRows, filter]);

  return (
    <GridTableLayout
      layoutState={layoutState}
      hideEditColumns
      tableProps={{ id: "projectBudgetTable", rows, columns }}
    />
  );
}

function createRows(rows: ProjectBudgetRow[], filter: ProjectBudgetFilter): GridDataRow<Row>[] {
  return [
    simpleHeader,
    {
      kind: "totals" as const,
      id: "totals",
      data: {
        currentBudgetInCents: rows.sum((r) => r.currentBudgetInCents),
        newBudgetInCents: rows.sum((r) => r.newBudgetInCents),
        // Any row without price data makes the total meaningless, so dash it out as well
        newPriceInCents: rows.some((r) => r.newPriceInCents === undefined)
          ? undefined
          : rows.sum((r) => r.newPriceInCents),
      },
    },
    ...rows
      .filter(({ costCode }) => !filter.costCodeIds?.nonEmpty || filter.costCodeIds.includes(costCode.id))
      .filter(({ newBudgetInCents }) => (filter.showChanges ? newBudgetInCents !== 0 : true))
      .map((r) => ({ kind: "costCode" as const, id: r.costCode.id, data: r })),
  ];
}

type ProjectBudgetFilter = { costCodeIds?: string[]; showChanges?: boolean };

/** The totals row, i.e. what every cost code in the configuration adds up to. */
type ProjectBudgetTotals = Omit<ProjectBudgetRow, "costCode">;

type Row =
  | { kind: "totals"; id: string; data: ProjectBudgetTotals }
  | { kind: "header"; id: string }
  | { kind: "costCode"; id: string; data: ProjectBudgetRow };

const columns: GridColumn<Row>[] = [
  column<Row>({
    totals: () => ({ content: "Total", css: Css.smSb.$ }),
    header: "Cost Code",
    costCode: ({ costCode }) => costCode.displayName,
    w: 2,
  }),
  numericColumn<Row>({
    totals: ({ currentBudgetInCents }) => <Price valueInCents={currentBudgetInCents} />,
    header: "Current Budget",
    costCode: ({ currentBudgetInCents }) => <Price valueInCents={currentBudgetInCents} />,
  }),
  numericColumn<Row>({
    totals: ({ newBudgetInCents }) => <ReviewImpactedPrice valueInCents={newBudgetInCents} />,
    header: "New Budget",
    costCode: ({ newBudgetInCents }) => <ReviewImpactedPrice valueInCents={newBudgetInCents} />,
  }),
  numericColumn<Row>({
    totals: ({ newBudgetInCents, newPriceInCents }) => markupOrDash(newBudgetInCents, newPriceInCents),
    header: "Markup",
    costCode: ({ newBudgetInCents, newPriceInCents }) => markupOrDash(newBudgetInCents, newPriceInCents),
  }),
  numericColumn<Row>({
    totals: ({ newPriceInCents }) => priceOrDash(newPriceInCents),
    header: "Price",
    costCode: ({ newPriceInCents }) => priceOrDash(newPriceInCents),
  }),
];

function markupOrDash(budgetInCents: number, priceInCents: number | undefined) {
  return priceInCents === undefined ? emptyCellDash : <MarkupSummary budget={budgetInCents} price={priceInCents} />;
}

function priceOrDash(valueInCents: number | undefined) {
  return valueInCents === undefined ? emptyCellDash : <ReviewImpactedPrice valueInCents={valueInCents} />;
}
